import {
  AlertTriangle,
  ArrowUpRight,
  Download,
  FileJson2,
  FileSpreadsheet,
  Printer,
} from "lucide-react";
import { StatusPill } from "../components/Ui";
import { POLICY_PACK } from "../data/cdmPolicyPack";
import {
  assessProject,
  riskCompletionIssues,
  workspaceMetrics,
} from "../lib/assessmentEngine";
import type { ProjectWorkspace } from "../types/domain";

const download = (filename: string, content: string, type: string) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const csvCell = (value: string | number) => `"${String(value).replaceAll('"', '""')}"`;

const riskCsv = (workspace: ProjectWorkspace) => {
  const header = [
    "Design element",
    "Lifecycle stage",
    "Hazard",
    "Affected people",
    "Elimination considered",
    "Design decision",
    "Residual risk",
    "Control",
    "Drawing or specification",
    "Owner",
    "Review status",
    "Reviewer",
    "Review date",
    "Quality gaps",
  ];
  const rows = workspace.risks.map((risk) => [
    risk.designElement,
    risk.lifecycleStage,
    risk.hazard,
    risk.affectedPeople,
    risk.eliminationConsidered,
    risk.designDecision,
    risk.residualRisk,
    risk.control,
    risk.drawingReference,
    risk.owner,
    risk.reviewStatus,
    risk.reviewer,
    risk.reviewDate,
    riskCompletionIssues(risk).join(" | "),
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
};

const dutyCsv = (workspace: ProjectWorkspace) => {
  const assessment = assessProject(workspace.project);
  const header = ["Provision", "Title", "Dutyholder", "Owner", "Due date", "Status", "Response", "Evidence IDs", "Official source"];
  const rows = assessment.triggeredRules.map((rule) => {
    const action = workspace.dutyActions.find((item) => item.ruleId === rule.id);
    return [
      rule.regulation,
      rule.title,
      rule.dutyHolder,
      action?.owner ?? "",
      action?.dueDate ?? "",
      action?.status ?? "open",
      action?.response ?? "",
      action?.evidenceIds.join(" | ") ?? "",
      rule.sourceUrl,
    ];
  });
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
};

const hirarcCsv = (workspace: ProjectWorkspace) => {
  const header = [
    "Activity",
    "Location",
    "Hazard",
    "Consequence",
    "Persons at risk",
    "Existing controls",
    "Initial likelihood",
    "Initial severity",
    "Initial score",
    "Recommended controls",
    "Control hierarchy",
    "PIC",
    "Due date",
    "Status",
    "Residual likelihood",
    "Residual severity",
    "Residual score",
    "Review trigger",
  ];
  const rows = workspace.hirarcAssessments.map((item) => [
    item.activity,
    item.location,
    item.hazard,
    item.consequence,
    item.personsAtRisk,
    item.existingControls,
    item.likelihood,
    item.severity,
    item.riskScore,
    item.recommendedControls,
    item.controlHierarchy,
    item.owner,
    item.dueDate,
    item.status,
    item.residualLikelihood,
    item.residualSeverity,
    item.residualRiskScore,
    item.reviewTrigger,
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
};

const controlCsv = (workspace: ProjectWorkspace) => {
  const header = [
    "Critical control",
    "Major accident scenario",
    "Bowtie side",
    "Hierarchy",
    "Performance standard",
    "Verification method",
    "Owner",
    "Frequency",
    "Health",
    "Last verified",
    "Next due",
    "Evidence IDs",
    "Risk IDs",
  ];
  const rows = workspace.criticalControls.map((item) => [
    item.title,
    item.majorAccidentScenario,
    item.controlType,
    item.hierarchy,
    item.performanceStandard,
    item.verificationMethod,
    item.owner,
    item.verificationFrequency,
    item.health,
    item.lastVerifiedDate,
    item.nextDueDate,
    item.evidenceIds.join(" | "),
    item.linkedRiskIds.join(" | "),
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
};

const evidenceCsv = (workspace: ProjectWorkspace) => {
  const header = [
    "Title",
    "Type",
    "Reference",
    "Owner",
    "Status",
    "Version",
    "Review date",
    "File name",
    "SHA-256",
    "Linked duty IDs",
    "Notes",
  ];
  const rows = workspace.evidence.map((item) => [
    item.title,
    item.type,
    item.reference,
    item.owner,
    item.status,
    item.version,
    item.reviewDate,
    item.fileName,
    item.checksum,
    item.linkedDutyIds.join(" | "),
    item.notes,
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
};

const auditCsv = (workspace: ProjectWorkspace) => {
  const header = ["Occurred at", "Actor", "Action", "Entity type", "Entity ID", "Summary"];
  const rows = workspace.auditTrail.map((item) => [
    item.occurredAt,
    item.actor,
    item.action,
    item.entityType,
    item.entityId,
    item.summary,
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
};

const slug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "design-safety-project";

export function ReportsView({ workspace }: { workspace: ProjectWorkspace }) {
  const assessment = assessProject(workspace.project);
  const metrics = workspaceMetrics(workspace);
  const projectSlug = slug(workspace.project.name);
  const limitations = [
    ...assessment.warnings,
    ...workspace.risks.flatMap((risk) =>
      riskCompletionIssues(risk).map(
        (issue) => `${risk.designElement || "Untitled design risk"}: ${issue}`,
      ),
    ),
  ];

  const outputs = [
    {
      title: "Project duty assessment",
      description: "Applicability facts, notification indication, roles and triggered duties.",
      readiness: assessment.warnings.length ? "Needs review" : "Ready to review",
    },
    {
      title: "Design-risk register",
      description: "Prevention decisions, residual risks, owners and controlled references.",
      readiness: workspace.risks.length ? `${workspace.risks.length} records` : "No records",
    },
    {
      title: "Expected-evidence schedule",
      description: "Duty-linked evidence expectations and current project records.",
      readiness: `${metrics.openDutyEvidence} gaps`,
    },
    {
      title: "Safety and Health File index",
      description: "Controlled index for appointments, design records, plans and handover evidence.",
      readiness: `${workspace.evidence.length} records`,
    },
    {
      title: "DOSH HIRARC register",
      description: "Activity hazards, risk scores, hierarchy controls, PIC and residual risk.",
      readiness: `${workspace.hirarcAssessments.length} assessments`,
    },
    {
      title: "Critical control verification register",
      description: "Fatal-risk scenarios, barrier standards, owners, health and evidence.",
      readiness: `${workspace.criticalControls.length} controls`,
    },
    {
      title: "Revision and change-impact register",
      description: "Controlled safety information linked to design decisions and duties.",
      readiness: `${workspace.revisionComparisons.length} comparisons`,
    },
    {
      title: "CDM deliverable set",
      description: "Graph-compiled PCI, appointments, JKKP 12 record, CPP, DRA and Safety and Health File.",
      readiness: `${workspace.deliverables.length} controlled outputs`,
    },
    {
      title: "Regulatory change-impact register",
      description: "Changed clauses, affected duties, linked risks, owners and accountable decisions.",
      readiness: `${workspace.regulatoryChanges.length} change records`,
    },
    {
      title: "Field verification register",
      description: "QR-addressable checks linking risks, controls, HIRARC, revisions and evidence.",
      readiness: `${workspace.fieldVerifications.length} field checks`,
    },
    {
      title: "Incident learning register",
      description: "Project events and external lessons linked to risks and critical controls.",
      readiness: `${workspace.incidents.length} learning records`,
    },
    {
      title: "Competence and training matrix",
      description: "Regulation 10 capability evidence, gaps, expiry and assigned learning.",
      readiness: `${workspace.competenceRecords.length} people`,
    },
    {
      title: "Assurance limitations",
      description: "Unknown, incomplete or unapproved information that must remain visible.",
      readiness: `${limitations.length} limitations`,
    },
    {
      title: "Accountable activity trail",
      description: "Timestamped record of project and assurance-record changes.",
      readiness: `${workspace.auditTrail.length} events`,
    },
  ];

  return (
    <>
      <section className="export-actions">
        <article>
          <span className="eyebrow">Controlled deliverable compiler</span>
          <h2>Generate the project pack from one connected evidence graph.</h2>
          <p>
            Exports preserve project inputs, policy version, HIRARC, controls, revisions, evidence,
            competence and unresolved limitations so every conclusion remains reproducible.
          </p>
        </article>
        <div className="button-stack">
          <button
            className="button button-secondary"
            onClick={() =>
              download(
                `${projectSlug}-workspace.json`,
                JSON.stringify(
                  {
                    exportedAt: new Date().toISOString(),
                    policyPack: POLICY_PACK,
                    assessment,
                    workspace,
                  },
                  null,
                  2,
                ),
                "application/json",
              )
            }
          >
            <FileJson2 size={18} /> Export controlled JSON
          </button>
          <button
            className="button button-secondary"
            onClick={() =>
              download(
                `${projectSlug}-design-risk-register.csv`,
                riskCsv(workspace),
                "text/csv;charset=utf-8",
              )
            }
          >
            <FileSpreadsheet size={18} /> Export risk register CSV
          </button>
          <button
            className="button button-secondary"
            onClick={() =>
              download(
                `${projectSlug}-regulatory-action-register.csv`,
                dutyCsv(workspace),
                "text/csv;charset=utf-8",
              )
            }
          >
            <FileSpreadsheet size={18} /> Export duty actions CSV
          </button>
          <button
            className="button button-secondary"
            onClick={() =>
              download(
                `${projectSlug}-hirarc-register.csv`,
                hirarcCsv(workspace),
                "text/csv;charset=utf-8",
              )
            }
          >
            <FileSpreadsheet size={18} /> Export HIRARC CSV
          </button>
          <button
            className="button button-secondary"
            onClick={() =>
              download(
                `${projectSlug}-critical-controls.csv`,
                controlCsv(workspace),
                "text/csv;charset=utf-8",
              )
            }
          >
            <FileSpreadsheet size={18} /> Export controls CSV
          </button>
          <button
            className="button button-secondary"
            onClick={() =>
              download(
                `${projectSlug}-evidence-register.csv`,
                evidenceCsv(workspace),
                "text/csv;charset=utf-8",
              )
            }
          >
            <FileSpreadsheet size={18} /> Export evidence CSV
          </button>
          <button
            className="button button-secondary"
            onClick={() =>
              download(
                `${projectSlug}-audit-trail.csv`,
                auditCsv(workspace),
                "text/csv;charset=utf-8",
              )
            }
          >
            <FileSpreadsheet size={18} /> Export audit CSV
          </button>
          <button className="button button-primary" onClick={() => window.print()}>
            <Printer size={18} /> Print assurance report
          </button>
        </div>
      </section>

      <section className="output-grid">
        {outputs.map((output, index) => (
          <article className="output-card" key={output.title}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <div>
              <h3>{output.title}</h3>
              <p>{output.description}</p>
            </div>
            <StatusPill
              status={
                output.readiness.includes("Needs") ||
                output.readiness.includes("No ") ||
                (!output.readiness.startsWith("0") && output.readiness.includes("gap"))
                  ? "warning"
                  : "neutral"
              }
            >
              {output.readiness}
            </StatusPill>
          </article>
        ))}
      </section>

      <section className="report-sheet">
        <header className="report-cover">
          <div className="report-logo">DS</div>
          <div>
            <small>DESIGN SAFETY MY · CONTROLLED ASSURANCE RECORD</small>
            <h2>Project Design-Safety Assurance Report</h2>
            <p>{workspace.project.name || "Unnamed project"}</p>
          </div>
          <dl>
            <div>
              <dt>Generated</dt>
              <dd>{new Date().toLocaleDateString("en-MY")}</dd>
            </div>
            <div>
              <dt>Policy pack</dt>
              <dd>{POLICY_PACK.version}</dd>
            </div>
          </dl>
        </header>

        <div className="report-section">
          <span className="report-section-number">01</span>
          <div>
            <h3>Project basis</h3>
            <div className="report-facts">
              <div>
                <small>Client</small>
                <strong>{workspace.project.clientName || "Not confirmed"}</strong>
              </div>
              <div>
                <small>Location</small>
                <strong>{workspace.project.location || "Not confirmed"}</strong>
              </div>
              <div>
                <small>Jurisdiction</small>
                <strong>
                  {(workspace.project.regulatoryRegion ?? "unresolved").replaceAll("-", " ")}
                </strong>
              </div>
              <div>
                <small>Work type</small>
                <strong>{workspace.project.workType.replace("-", " ")}</strong>
              </div>
              <div>
                <small>Contractors</small>
                <strong>{workspace.project.contractorCount ?? "Unknown"}</strong>
              </div>
            </div>
            <p>{workspace.project.description || "Project scope has not been recorded."}</p>
          </div>
        </div>

        <div className="report-section">
          <span className="report-section-number">02</span>
          <div>
            <h3>Regulatory assessment</h3>
            <p>
              {assessment.cdmRules.length} CDM duties and{" "}
              {assessment.relatedRules.length + assessment.approvalGateways.length} related
              regulatory checks are triggered. The recorded thresholds indicate that the project{" "}
              {assessment.notifiable ? "is" : "is not"} currently notifiable. The named project
              dutyholder retains the decision and its supporting evidence.
            </p>
            <div className="report-duty-list">
              {assessment.triggeredRules.map((rule) => (
                <div key={rule.id}>
                  <strong>{rule.regulation}</strong>
                  <span>{rule.title}</span>
                  <small>
                    {rule.dutyHolder.replace("-", " ")} · {rule.instrument}
                  </small>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="report-section">
          <span className="report-section-number">03</span>
          <div>
            <h3>Design-risk decisions</h3>
            {workspace.risks.length === 0 ? (
              <p>No design-risk decisions have been recorded.</p>
            ) : (
              workspace.risks.map((risk) => (
                <div className="report-risk" key={risk.id}>
                  <div>
                    <strong>{risk.designElement || "Untitled design element"}</strong>
                    <StatusPill
                      status={risk.reviewStatus === "approved" ? "success" : "warning"}
                    >
                      {risk.reviewStatus}
                    </StatusPill>
                  </div>
                  <p>{risk.hazard || "Hazard statement missing."}</p>
                  <dl>
                    <div>
                      <dt>Decision</dt>
                      <dd>{risk.designDecision || "Not recorded"}</dd>
                    </div>
                    <div>
                      <dt>Residual control</dt>
                      <dd>{risk.control || "Not recorded"}</dd>
                    </div>
                    <div>
                      <dt>Reference</dt>
                      <dd>{risk.drawingReference || "Missing"}</dd>
                    </div>
                  </dl>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="report-section">
          <span className="report-section-number">04</span>
          <div>
            <h3>Evidence index</h3>
            <div className="report-evidence">
              {workspace.evidence.length === 0 ? (
                <p>No evidence records have been added.</p>
              ) : (
                workspace.evidence.map((item) => (
                  <div key={item.id}>
                    <span>
                      <strong>{item.title}</strong>
                      <small>{item.reference || "No reference"}</small>
                    </span>
                    <StatusPill
                      status={
                        item.status === "verified"
                          ? "success"
                          : item.status === "missing"
                            ? "danger"
                            : "warning"
                      }
                    >
                      {item.status}
                    </StatusPill>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="report-section">
          <span className="report-section-number">05</span>
          <div>
            <h3>HIRARC and critical controls</h3>
            <div className="report-facts">
              <div><small>HIRARC assessments</small><strong>{workspace.hirarcAssessments.length}</strong></div>
              <div><small>High residual risks</small><strong>{metrics.hirarcHighRisks}</strong></div>
              <div><small>Critical controls</small><strong>{workspace.criticalControls.length}</strong></div>
              <div><small>Controls needing action</small><strong>{metrics.controlsDue}</strong></div>
            </div>
            {workspace.hirarcAssessments.map((item) => (
              <div className="report-risk" key={item.id}>
                <div>
                  <strong>{item.activity || item.hazard || "Untitled HIRARC assessment"}</strong>
                  <StatusPill
                    status={item.residualRiskScore >= 15 ? "danger" : item.residualRiskScore >= 5 ? "warning" : "success"}
                  >
                    residual {item.residualRiskScore}
                  </StatusPill>
                </div>
                <p>{item.hazard || "Hazard not recorded."} · {item.consequence || "Consequence not recorded."}</p>
                <dl>
                  <div><dt>Existing controls</dt><dd>{item.existingControls || "Missing"}</dd></div>
                  <div><dt>Required controls</dt><dd>{item.recommendedControls || "Missing"}</dd></div>
                  <div><dt>PIC / due</dt><dd>{item.owner || "Unassigned"} · {item.dueDate || "No date"}</dd></div>
                </dl>
              </div>
            ))}
            {workspace.criticalControls.map((control) => (
              <div className="report-risk" key={control.id}>
                <div><strong>{control.title || "Untitled critical control"}</strong><StatusPill status={control.health === "effective" ? "success" : "warning"}>{control.health}</StatusPill></div>
                <p>{control.majorAccidentScenario || "Major accident scenario not recorded."}</p>
                <dl>
                  <div><dt>Performance standard</dt><dd>{control.performanceStandard || "Missing"}</dd></div>
                  <div><dt>Verification</dt><dd>{control.verificationMethod || "Missing"}</dd></div>
                  <div><dt>Owner</dt><dd>{control.owner || "Unassigned"}</dd></div>
                </dl>
              </div>
            ))}
          </div>
        </div>

        <div className="report-section">
          <span className="report-section-number">06</span>
          <div>
            <h3>Safety information and capability</h3>
            <div className="report-facts">
              <div><small>Controlled revisions</small><strong>{workspace.documentRevisions.length}</strong></div>
              <div><small>Learning records</small><strong>{workspace.incidents.length}</strong></div>
              <div><small>Competence records</small><strong>{workspace.competenceRecords.length}</strong></div>
              <div><small>Training assignments</small><strong>{workspace.trainingAssignments.length}</strong></div>
            </div>
            <div className="report-evidence">
              {workspace.documentRevisions.map((revision) => (
                <div key={revision.id}>
                  <span>
                    <strong>{revision.documentNumber || "No number"} · {revision.revision}</strong>
                    <small>{revision.title || "Untitled revision"} · {revision.changeSummary || "No change summary"}</small>
                  </span>
                  <StatusPill status={revision.status === "approved" ? "success" : "warning"}>
                    {revision.status}
                  </StatusPill>
                </div>
              ))}
              {workspace.competenceRecords.map((record) => (
                <div key={record.id}>
                  <span>
                    <strong>{record.personName || "Unnamed person"} · {record.role || "No role"}</strong>
                    <small>{record.organisation || "No organisation"} · {record.evidenceReference || "No evidence reference"}</small>
                  </span>
                  <StatusPill status={record.status === "competent" ? "success" : "warning"}>
                    {record.status}
                  </StatusPill>
                </div>
              ))}
              {workspace.approvals.map((approval) => (
                <div key={approval.id}>
                  <span>
                    <strong>{approval.entityType} · {approval.step}</strong>
                    <small>{approval.requester || "No requester"} → {approval.reviewer || "No reviewer"}</small>
                  </span>
                  <StatusPill status={approval.status === "approved" ? "success" : approval.status === "rejected" ? "danger" : "warning"}>
                    {approval.status}
                  </StatusPill>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="report-section">
          <span className="report-section-number">07</span>
          <div>
            <h3>Change, deliverables and field assurance</h3>
            <div className="report-facts">
              <div><small>Revision comparisons</small><strong>{workspace.revisionComparisons.length}</strong></div>
              <div><small>Reopened risks</small><strong>{workspace.revisionComparisons.flatMap((item) => item.reopenedRiskIds).length}</strong></div>
              <div><small>Controlled deliverables</small><strong>{workspace.deliverables.length}</strong></div>
              <div><small>Regulatory changes</small><strong>{workspace.regulatoryChanges.length}</strong></div>
              <div><small>Field checks</small><strong>{workspace.fieldVerifications.length}</strong></div>
              <div><small>Open BCF/page issues</small><strong>{workspace.safetyIssuePins.filter((item) => item.status !== "complete").length}</strong></div>
            </div>
            <div className="report-evidence">
              {workspace.deliverables.map((deliverable) => (
                <div key={deliverable.id}>
                  <span>
                    <strong>{deliverable.title} · {deliverable.reference}</strong>
                    <small>{deliverable.revision} · {deliverable.sections.filter((item) => item.status === "missing").length} missing section(s)</small>
                  </span>
                  <StatusPill status={deliverable.status === "issued" ? "success" : "warning"}>{deliverable.status}</StatusPill>
                </div>
              ))}
              {workspace.regulatoryChanges.map((change) => (
                <div key={change.id}>
                  <span>
                    <strong>{change.instrument || "Untitled regulatory change"}</strong>
                    <small>{change.previousPackVersion || "unversioned"} → {change.newPackVersion || "unversioned"} · {change.impactedDutyIds.length} duties · {change.impactedRiskIds.length} risks</small>
                  </span>
                  <StatusPill status={change.impactStatus === "closed" ? "success" : "warning"}>{change.impactStatus}</StatusPill>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="report-section">
          <span className="report-section-number">08</span>
          <div>
            <h3>Accountable activity trail</h3>
            {workspace.auditTrail.length === 0 ? (
              <p>No assurance activity has been recorded.</p>
            ) : (
              <div className="report-evidence">
                {[...workspace.auditTrail]
                  .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
                  .slice(0, 20)
                  .map((event) => (
                    <div key={event.id}>
                      <span>
                        <strong>{event.summary}</strong>
                        <small>{event.actor} · {new Date(event.occurredAt).toLocaleString("en-MY")}</small>
                      </span>
                      <small>{event.action}</small>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        <div className="report-section limitations-section">
          <span className="report-section-number">09</span>
          <div>
            <h3>Limitations and accountable decisions</h3>
            {limitations.length === 0 ? (
              <p>No structured limitations were generated.</p>
            ) : (
              <ul>
                {limitations.map((limitation, index) => (
                  <li key={`${limitation}-${index}`}>
                    <AlertTriangle size={15} />
                    {limitation}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <footer className="report-footer">
          <span>
            <Download size={15} /> Controlled assurance output
          </span>
          <a href={POLICY_PACK.sourceUrl} target="_blank" rel="noreferrer">
            Official source <ArrowUpRight size={14} />
          </a>
        </footer>
      </section>
    </>
  );
}
